const DEFAULT_FROM_PREFIXES = [
  "https://s3.ap-south-1.amazonaws.com/prepseed",
  "https://prepseed.s3.ap-south-1.amazonaws.com",
  "http://s3.ap-south-1.amazonaws.com/prepseed",
  "http://prepseed.s3.ap-south-1.amazonaws.com",
];

const DEFAULT_TO_PREFIX = "https://prepseed-files.prepseed.com";
const DEFAULT_S3_PREFIX = "https://s3.ap-south-1.amazonaws.com/prepseed";

/**
 * Convert known S3 URL prefixes to CloudFront (matches backend helper).
 */
export default function convertS3ToCloudFront(
  url,
  toPrefix = DEFAULT_TO_PREFIX,
  fromPrefixes = DEFAULT_FROM_PREFIXES,
) {
  const input = String(url ?? "");
  for (const fromPrefix of fromPrefixes) {
    if (fromPrefix && toPrefix && input.startsWith(fromPrefix)) {
      return toPrefix + input.slice(fromPrefix.length);
    }
  }
  return input;
}

/**
 * Convert CloudFront URLs back to direct S3 for fetches (e.g. PPT image download).
 */
export function convertCloudFrontToS3(
  url,
  toPrefix = DEFAULT_S3_PREFIX,
  fromPrefixes = [
    DEFAULT_TO_PREFIX,
    "http://prepseed-files.prepseed.com",
  ],
) {
  const input = String(url ?? "");
  for (const fromPrefix of fromPrefixes) {
    if (fromPrefix && toPrefix && input.startsWith(fromPrefix)) {
      return toPrefix + input.slice(fromPrefix.length);
    }
  }
  return input;
}

/**
 * Normalize inventory image URLs for browser fetch during PPT generation.
 */
export function prepareInventoryImageFetchUrl(url) {
  if (!url || typeof url !== "string") return url;

  let normalized = convertCloudFrontToS3(url.trim());

  try {
    const parsed = new URL(normalized);
    parsed.searchParams.delete("_");
    for (const key of [...parsed.searchParams.keys()]) {
      if (key.startsWith("cachebuster")) {
        parsed.searchParams.delete(key);
      }
    }
    parsed.searchParams.delete("response-content-disposition");
    const search = parsed.searchParams.toString();
    normalized = `${parsed.origin}${parsed.pathname}${search ? `?${search}` : ""}`;
  } catch {
    // keep best-effort normalized value
  }

  return normalized;
}

/**
 * Build a viewable URL: convert S3 → CloudFront and add inline disposition when safe.
 */
export function getViewableFileUrl(url) {
  if (!url) return url;

  const normalizedUrl = convertS3ToCloudFront(url);
  const isPresigned =
    /X-Amz-Signature=|X-Amz-Algorithm=|X-Amz-Credential=/.test(normalizedUrl);
  const isDirectS3 = /amazonaws\.com/i.test(normalizedUrl);

  if (
    !isPresigned &&
    !isDirectS3 &&
    !/response-content-disposition=/.test(normalizedUrl)
  ) {
    return `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}response-content-disposition=inline`;
  }

  return normalizedUrl;
}

/**
 * Open a file URL in a new tab with CloudFront conversion applied.
 */
export function openFileInNewTab(url) {
  if (!url) return;
  window.open(getViewableFileUrl(url), "_blank", "noopener,noreferrer");
}
